import { saveAuthenticationData, useDomain, useLogin } from "@netfront/gelada-identity-library";
import { useToast } from "@netfront/ekardo-content-library";
import { useFormik } from "formik";
import { useRouter } from "next/router";
import * as yup from 'yup';
import { Button, Input, PasswordInput } from "@netfront/ui-library";
import { ILoginFormFields } from "./LoginForm.interfaces";
import Link from "next/link";
import { ICookieOptionalAttributes, useToggle } from "@netfront/common-library";

interface LoginFormProps {
  onCompleted?: () => void;
}

export const LoginForm = ({ onCompleted }: LoginFormProps = {}) => {
  const { push, query: { redirect }} = useRouter();
  const { handleToastError } = useToast();
  const { getDomain } = useDomain();
  const { isToggled: isLoggingInLoading, toggle: toggleLoggingInLoading } = useToggle();

  const navigateToUrlAfterLoginCompleted = redirect ? decodeURIComponent(String(redirect)) : '/dashboard';

  const formik = useFormik<ILoginFormFields>({
    initialValues: {
      email: '',
      password: ''
    },
    validationSchema: yup.object().shape({
      email: yup.string().email('Please enter a valid email address').required('Please enter in a valid email address'),
      password: yup.string().required('Please enter in a password'),
    }),
    onSubmit: async ({ email, password }: ILoginFormFields) => {
      handleLogin({
        projectId: String(process.env.REACT_APP_PROJECT_ID),
        password,
        login: email
      })
    }
  });

  const { handleLogin } = useLogin({
    onCompleted: ({ accessToken, refreshToken, user }) => {
      if (!user) return;

      const domain = getDomain() || undefined;
      const isSecure = process.env.REACT_APP_COOKIE_ATTRIBUTE_SECURE === 'true';
      const sameSite: ICookieOptionalAttributes['sameSite'] = isSecure ? 'None' : 'Lax';

      const cookieAttributes: ICookieOptionalAttributes = { sameSite, secure: isSecure, ...(domain && { domain }) };

      saveAuthenticationData({
        accessToken,
        accessTokenOptionalCookieAttributes: cookieAttributes,
        refreshToken,
        refreshTokenOptionalCookieAttributes: cookieAttributes,
        user,
        userOptionalCookieAttributes: cookieAttributes,
      });

      if (onCompleted) {
        onCompleted();
      } else {
        push(navigateToUrlAfterLoginCompleted);
      }
    },
    onError: error => {
      if (!error) {
        return;
      }

      toggleLoggingInLoading();
  
      handleToastError({
        error,
        shouldUseFriendlyErrorMessage: true,
      });
    },
  });

  const { errors, touched, handleBlur, handleChange, handleSubmit } = formik;

  return (
    <form onSubmit={handleSubmit}>
      <Input
        errorMessage={touched.email && errors.email ? errors.email : ''}
        id='email'
        labelText='Email'
        name='email'
        placeholder='Enter email'
        onChange={handleChange}
        onBlur={handleBlur}
        type='email'
        isRequired
      />

      <PasswordInput
        errorMessage={touched.password && errors.password ? errors.password : ''}
        id='password'
        labelText='Password'
        name='password'
        placeholder='Enter password'
        isRequired
        onChange={handleChange}
        onBlur={handleBlur}
      />

      <Button
        additionalClassNames='w-full'
        variant='tertiary'
        text='Sign in'
        type='submit'
        isLoading={isLoggingInLoading}
        onClick={toggleLoggingInLoading}
      />

      <hr className="my-8" />

      <div className="grid gap-2">
        <p>Don't have an account? <Link href="/register" className="color-green-500 weight-600">Register</Link></p>
        <Link href="/forgot-password" className="block color-green-500 weight-600">Forgot password</Link>
      </div>
    
    </form>
  )
};